import { useState } from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Mail,
  Eye,
  EyeOff,
  Check,
  Loader2,
  Send,
  AlertCircle,
  CheckCircle2
} from "lucide-react";
import { cn } from "@/lib/utils";

export function EmailSettings() {
  const [senderName, setSenderName] = useState("Allen Analysis");
  const [senderEmail, setSenderEmail] = useState("noreply@allenanalysis.com");
  const [replyToEmail, setReplyToEmail] = useState("support@allenanalysis.com");
  const [resendApiKey, setResendApiKey] = useState("");
  const [showApiKey, setShowApiKey] = useState(false);
  const [emailFooter, setEmailFooter] = useState("Thank you for being a subscriber!\n\nAllen Analysis\n123 Main Street, Suite 100\nNew York, NY 10001");
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<"success" | "error" | null>(null);

  const handleSave = async () => {
    setIsSaving(true);
    await new Promise((resolve) => setTimeout(resolve, 1500));
    setIsSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleTestConnection = async () => {
    if (!resendApiKey) {
      setTestResult("error");
      setTimeout(() => setTestResult(null), 3000);
      return;
    }

    setIsTesting(true);
    setTestResult(null);
    // Simulate API test
    await new Promise((resolve) => setTimeout(resolve, 2000));
    setIsTesting(false);
    // Randomly succeed or fail for demo
    setTestResult(Math.random() > 0.3 ? "success" : "error");
    setTimeout(() => setTestResult(null), 5000);
  };

  const maskApiKey = (key: string) => {
    if (!key) return "";
    if (key.length <= 8) return "*".repeat(key.length);
    return key.slice(0, 4) + "*".repeat(key.length - 8) + key.slice(-4);
  };

  return (
    <div className="space-y-6">
      {/* Sender Configuration */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Sender Configuration
            </CardTitle>
            <CardDescription>
              Configure how your emails appear to recipients
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="senderName">Sender Name</Label>
                <Input
                  id="senderName"
                  value={senderName}
                  onChange={(e) => setSenderName(e.target.value)}
                  placeholder="Your Company Name"
                />
                <p className="text-xs text-muted-foreground">
                  The name that appears in recipients' inboxes
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="senderEmail">Sender Email</Label>
                <Input
                  id="senderEmail"
                  type="email"
                  value={senderEmail}
                  onChange={(e) => setSenderEmail(e.target.value)}
                  placeholder="noreply@example.com"
                />
                <p className="text-xs text-muted-foreground">
                  Must be a verified domain
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="replyTo">Reply-To Email</Label>
              <Input
                id="replyTo"
                type="email"
                value={replyToEmail}
                onChange={(e) => setReplyToEmail(e.target.value)}
                placeholder="support@example.com"
                className="max-w-md"
              />
              <p className="text-xs text-muted-foreground">
                Where replies will be sent when subscribers respond
              </p>
            </div>

            {/* Email Preview */}
            <div className="mt-6 p-4 rounded-xl bg-muted/50 space-y-2">
              <p className="text-sm font-medium">Email Preview</p>
              <div className="bg-background rounded-lg p-4 border border-border">
                <div className="flex items-center gap-3 pb-3 border-b border-border">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-sm font-semibold text-primary">
                      {senderName.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-sm">{senderName || "Sender Name"}</p>
                    <p className="text-xs text-muted-foreground">
                      {senderEmail || "email@example.com"}
                    </p>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mt-3">
                  Reply-to: {replyToEmail || "reply@example.com"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Resend API Configuration */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Email Provider (Resend)</CardTitle>
            <CardDescription>
              Connect your Resend account to send emails
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="apiKey">Resend API Key</Label>
              <div className="flex gap-2 max-w-lg">
                <div className="relative flex-1">
                  <Input
                    id="apiKey"
                    type={showApiKey ? "text" : "password"}
                    value={showApiKey ? resendApiKey : maskApiKey(resendApiKey)}
                    onChange={(e) => {
                      if (showApiKey) {
                        setResendApiKey(e.target.value);
                      }
                    }}
                    onFocus={() => setShowApiKey(true)}
                    placeholder="re_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    className="pr-10 font-mono text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => setShowApiKey(!showApiKey)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showApiKey ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
                <Button
                  variant="outline"
                  onClick={handleTestConnection}
                  disabled={isTesting || !resendApiKey}
                  className="min-w-32"
                >
                  {isTesting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Testing...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Test
                    </>
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Get your API key from{" "}
                <a
                  href="https://resend.com/api-keys"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  resend.com/api-keys
                </a>
              </p>

              {/* Test Result */}
              {testResult && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={cn(
                    "flex items-center gap-2 p-3 rounded-lg mt-2",
                    testResult === "success"
                      ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                      : "bg-red-500/10 text-red-600 dark:text-red-400"
                  )}
                >
                  {testResult === "success" ? (
                    <>
                      <CheckCircle2 className="w-4 h-4" />
                      <span className="text-sm font-medium">
                        Connection successful! Your API key is valid.
                      </span>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="w-4 h-4" />
                      <span className="text-sm font-medium">
                        Connection failed. Please check your API key.
                      </span>
                    </>
                  )}
                </motion.div>
              )}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Email Footer */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Email Footer</CardTitle>
            <CardDescription>
              Customize the footer that appears at the bottom of all emails
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="footer">Footer Content</Label>
              <textarea
                id="footer"
                value={emailFooter}
                onChange={(e) => setEmailFooter(e.target.value)}
                className={cn(
                  "w-full min-h-32 px-3 py-2 text-sm rounded-md border border-input bg-background",
                  "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
                  "placeholder:text-muted-foreground resize-y"
                )}
                placeholder="Enter your email footer text..."
              />
              <p className="text-xs text-muted-foreground">
                Include your company address for CAN-SPAM compliance
              </p>
            </div>

            {/* Footer Preview */}
            <div className="p-4 rounded-xl bg-muted/50 space-y-2">
              <p className="text-sm font-medium">Footer Preview</p>
              <div className="bg-background rounded-lg p-4 border border-border">
                <div className="border-t border-dashed border-border pt-4 mt-4">
                  <p className="text-xs text-muted-foreground whitespace-pre-line">
                    {emailFooter || "Your footer will appear here..."}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Save Button */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="flex justify-end"
      >
        <Button
          onClick={handleSave}
          disabled={isSaving}
          className="min-w-32"
        >
          {isSaving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : saved ? (
            <>
              <Check className="w-4 h-4 mr-2" />
              Saved!
            </>
          ) : (
            "Save Changes"
          )}
        </Button>
      </motion.div>
    </div>
  );
}
